import {
    ApiBaseService,
    type EnhancedFetch,
    type EnhancedFetchOptions,
    type PaginatedFetch,
    type PaginatedFetchOptions,
} from "./ApiBaseService";
import type { App, AppWithRelations } from "~/models/App";
import type { ApiResponse } from "~/types/ApiResponse";
import AppTriggerService from "~/services/AppTriggerService";
import type { UnwrapRef } from "vue";
import type { FetchOptions } from "ofetch";

export default class AppService extends ApiBaseService {
    public list(options?: Partial<PaginatedFetchOptions>): PaginatedFetch<App> {
        return this.paginate<App>(
            this.route("api.apps.index", "/apps"),
            options as PaginatedFetchOptions,
        );
    }

    public triggers(uuid: string): AppTriggerService {
        return new AppTriggerService(uuid, this.getConfig());
    }

    public find(
        uuid: string,
        options?: EnhancedFetchOptions,
    ): EnhancedFetch<AppWithRelations> {
        return this.enhancedFetch<AppWithRelations>(
            this.route("api.apps.show", "/apps/{app}", {
                app: uuid,
            }),
            options,
        );
    }

    public findRoute(uuid: string) {
        return this.route("api.apps.show", "/apps/{app}", {
            app: uuid,
        });
    }

    public findRequest(
        uuid: string,
        options?: FetchOptions,
    ): Promise<ApiResponse<AppWithRelations>> {
        return this.request(this.findRoute(uuid), options);
    }

    public create(data?: Partial<App>): Promise<ApiResponse<AppWithRelations>> {
        return this.request(this.route("api.apps.store", "/apps"), {
            method: "POST",
            body: data,
        });
    }

    public update(
        uuid: string,
        data: Partial<App>,
    ): Promise<ApiResponse<AppWithRelations>> {
        return this.request(
            this.route("api.apps.store", "/apps/{app}", {
                app: uuid,
            }),
            {
                method: "POST",
                body: jsonToFormData(data),
                query: {
                    _method: "PATCH",
                },
            },
        );
    }

    public delete(uuid: string): Promise<null> {
        return this.request(
            this.route("api.apps.destroy", "/apps/{app}", {
                app: uuid,
            }),
            {
                method: "DELETE",
            },
        );
    }

    public getActionConfig<T>(
        app: string,
        action: string,
        body: Record<string, any> | UnwrapRef<Record<string, any>>,
    ): Promise<ApiResponse<T>> {
        return this.request<ApiResponse<T>>(
            this.route("api.apps.action-config", "/{app}/{action}/config", {
                app,
                action,
            }),
            {
                method: "POST",
                body: body && body.value ? body.value : body,
            },
        );
    }

    public install(uuid: string): Promise<ApiResponse<AppWithRelations>> {
        return this.request(
            this.route("api.apps.install", "/apps/{app}/install", {
                app: uuid,
            }),
            {
                method: "POST",
            },
        );
    }

    public uninstall(uuid: string): Promise<ApiResponse<AppWithRelations>> {
        return this.request(
            this.route("api.apps.uninstall", "/apps/{app}/uninstall", {
                app: uuid,
            }),
            {
                method: "DELETE",
            },
        );
    }
}
